/**
 * Ivanov API Client
 * ------------------------------------------------------------------
 * Foundational, reusable communication layer between the Shopify
 * theme and the custom Ivanov Jewelry backend.
 *
 * This file is intended to become the ONLY place in the theme that
 * talks to the backend over HTTP. Feature-specific code (diamond
 * search, etc.) should call into `window.IvanovAPI` instead of using
 * `fetch` directly.
 *
 * Configuration:
 *   The backend base URL is never hardcoded here. It must be provided
 *   by the theme via a global configuration object, injected in
 *   theme.liquid BEFORE this script runs:
 *
 *     window.IvanovConfig = {
 *       apiBaseUrl: "https://api.example.com"
 *     };
 *
 * Scope (this iteration):
 *   - Configurable base URL
 *   - Internal `request()` helper (GET + JSON POST, JSON parsing, error normalization)
 *   - Public methods: `searchDiamonds(filters)`, `normalizeDiamondId(id)`,
 *     `buildDiamondDetailUrl(id)`, `getDiamond(id, requestOptions?)`,
 *     `getDiamondCompare(id, requestOptions?)`, `getDiamondsBatch(ids, requestOptions?)`,
 *     `sendDropAHint(payload, requestOptions?)`
 *
 * Explicitly out of scope (future iterations):
 *   - Retries / backoff
 *   - Authentication headers
 *   - Response shape assumptions / caching
 */
(function (window) {
  'use strict';

  var DIAMONDS_PATH = '/diamonds';
  var DIAMOND_DETAIL_PAGE_PATH = '/pages/diamond-details';
  var DROP_A_HINT_PATH = '/drop-a-hint';
  var CANONICAL_PREFIX = 'DIAMOND/';
  var UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /**
   * Normalized error type thrown by the API client.
   * Carries the HTTP status (when available) and any parsed response
   * body so callers can make informed decisions without needing to
   * know about `fetch`/`Response` internals.
   */
  function IvanovAPIError(message, options) {
    options = options || {};
    var error = new Error(message);
    error.name = 'IvanovAPIError';
    error.status = options.status || null;
    error.data = options.data || null;
    error.cause = options.cause || null;
    return error;
  }

  /**
   * Resolves the configured backend base URL.
   * Fails gracefully (returns an empty string + console warning)
   * instead of throwing, so a missing config never breaks the page.
   */
  function getBaseUrl() {
    var config = window.IvanovConfig;
    var baseUrl = config && config.apiBaseUrl;

    if (!baseUrl) {
      console.warn(
        '[IvanovAPI] Missing window.IvanovConfig.apiBaseUrl. ' +
          'Requests will fail until it is set (see theme.liquid). ' +
          'Example: window.IvanovConfig = { apiBaseUrl: "https://api.example.com" };'
      );
      return '';
    }

    // Strip any trailing slash so paths can safely start with "/".
    return String(baseUrl).replace(/\/+$/, '');
  }

  /**
   * Builds a full request URL, appending query parameters when provided.
   * Falsy values (undefined/null/"") are dropped; array values become
   * repeated query params (e.g. { shape: ["round", "oval"] } -> ?shape=round&shape=oval).
   */
  function buildUrl(path, params) {
    var url = getBaseUrl() + path;

    if (params && typeof params === 'object') {
      var searchParams = new URLSearchParams();

      Object.keys(params).forEach(function (key) {
        var value = params[key];
        if (value === undefined || value === null || value === '') {
          return;
        }
        if (Array.isArray(value)) {
          value.forEach(function (item) {
            searchParams.append(key, item);
          });
        } else {
          searchParams.append(key, value);
        }
      });

      var queryString = searchParams.toString();
      if (queryString) {
        url += '?' + queryString;
      }
    }

    return url;
  }

  /**
   * Prefer a string Error.message. Keep structured bodies on `error.data`
   * (e.g. `{ error: { code, message } }`) for callers — never stringify
   * those objects into shopper-facing UI here.
   */
  function resolveErrorMessage(path, status, data) {
    if (data && typeof data.message === 'string' && data.message) {
      return data.message;
    }
    if (data && typeof data.error === 'string' && data.error) {
      return data.error;
    }
    if (
      data &&
      data.error &&
      typeof data.error === 'object' &&
      typeof data.error.message === 'string' &&
      data.error.message
    ) {
      return data.error.message;
    }
    return '[IvanovAPI] Request to ' + path + ' failed with status ' + status;
  }

  /**
   * Internal request helper. Not exposed on window.IvanovAPI.
   * Handles URL construction, optional JSON body (POST), JSON parsing,
   * and error normalization. No retries, no auth.
   */
  function request(path, options) {
    options = options || {};
    var method = options.method || 'GET';
    var url = buildUrl(path, options.params);
    var headers = Object.assign({ Accept: 'application/json' }, options.headers);

    var fetchOptions = {
      method: method,
      headers: headers,
    };
    if (options.signal) {
      fetchOptions.signal = options.signal;
    }
    if (options.body !== undefined && options.body !== null) {
      if (typeof options.body === 'string') {
        fetchOptions.body = options.body;
      } else {
        if (!headers['Content-Type'] && !headers['content-type']) {
          headers['Content-Type'] = 'application/json';
        }
        fetchOptions.body = JSON.stringify(options.body);
      }
    }

    return fetch(url, fetchOptions)
      .catch(function (networkError) {
        // Intentional cancellation must stay AbortError so Diamond Search
        // can ignore it. Do not wrap it as a shopper-visible API failure.
        if (networkError && networkError.name === 'AbortError') {
          throw networkError;
        }
        throw new IvanovAPIError(
          '[IvanovAPI] Network error while requesting ' + path + ': ' + networkError.message,
          { cause: networkError }
        );
      })
      .then(function (response) {
        return response.text().then(function (rawBody) {
          var data = null;

          if (rawBody) {
            try {
              data = JSON.parse(rawBody);
            } catch (parseError) {
              throw new IvanovAPIError(
                '[IvanovAPI] Failed to parse JSON response from ' + path,
                { status: response.status, cause: parseError }
              );
            }
          }

          if (!response.ok) {
            throw new IvanovAPIError(resolveErrorMessage(path, response.status, data), {
              status: response.status,
              data: data,
            });
          }

          return data;
        });
      });
  }

  /**
   * Searches diamonds via the backend.
   *
   * @param {Object} [filters] - Arbitrary query filters (shape, carat, color, etc.).
   *                              Passed through as-is as query parameters.
   * @param {Object} [requestOptions] - Optional fetch options. `signal`
   *                              (AbortSignal) cancels this request only.
   * @returns {Promise<*>} Parsed JSON response. Shape is intentionally NOT
   *                        assumed/validated here.
   */
  function searchDiamonds(filters, requestOptions) {
    requestOptions = requestOptions || {};
    return request(DIAMONDS_PATH, {
      method: 'GET',
      params: filters || {},
      signal: requestOptions.signal,
    });
  }

  /**
   * One safe decode. Malformed percent-sequences keep the original string
   * so a bad encoding cannot throw before the 400 path.
   */
  function decodeOnce(value) {
    try {
      return decodeURIComponent(value);
    } catch (err) {
      return value;
    }
  }

  /**
   * Normalizes inbound diamond identity to the platform pair:
   *   { bareUuid, canonicalId: "DIAMOND/{uuid}" }
   *
   * Accepts bare UUID, DIAMOND/{uuid} (prefix case-insensitive), and the
   * same values after one safe decode (e.g. DIAMOND%2F{uuid}).
   * Throws IvanovAPIError status 400 for missing/malformed input.
   */
  function normalizeDiamondId(id) {
    if (id == null) {
      throw new IvanovAPIError('[IvanovAPI] Invalid diamond id.', { status: 400 });
    }

    var raw = String(id).trim();
    if (!raw) {
      throw new IvanovAPIError('[IvanovAPI] Invalid diamond id.', { status: 400 });
    }

    var decoded = String(decodeOnce(raw)).trim();
    var bareUuid = decoded;

    if (/^diamond\//i.test(decoded)) {
      bareUuid = decoded.replace(/^diamond\//i, '').trim();
    }

    if (!UUID_RE.test(bareUuid)) {
      throw new IvanovAPIError('[IvanovAPI] Invalid diamond id.', { status: 400 });
    }

    return {
      bareUuid: bareUuid,
      canonicalId: CANONICAL_PREFIX + bareUuid,
    };
  }

  /**
   * Builds the canonical storefront Diamond Detail URL.
   * Accepts exactly the same identity forms as normalizeDiamondId and lets
   * that function throw IvanovAPIError status 400 for invalid input.
   */
  function buildDiamondDetailUrl(id) {
    var identity = normalizeDiamondId(id);
    return DIAMOND_DETAIL_PAGE_PATH + '?id=' + encodeURIComponent(identity.bareUuid);
  }

  /**
   * Fetches one diamond by identity from GET /diamonds/{bareUuid}.
   * Returns the parsed Stage 5D Detail response as-is (no mapDiamondItem).
   *
   * @param {string} id - Bare UUID, DIAMOND/{uuid}, or one-safe-decode form.
   * @param {Object} [requestOptions] - Optional fetch options. `signal`
   *                              (AbortSignal) cancels this request only.
   * @returns {Promise<*>} Parsed JSON Detail response.
   */
  function getDiamond(id, requestOptions) {
    requestOptions = requestOptions || {};
    var identity = normalizeDiamondId(id);
    return request(DIAMONDS_PATH + '/' + identity.bareUuid, {
      method: 'GET',
      signal: requestOptions.signal,
    });
  }

  /**
   * Fetches automatic Compare Similar Diamonds for one reference stone.
   * GET /diamonds/compare?id={bareUuid}
   * Returns the parsed backend payload as-is. Does not score or rewrite slots.
   *
   * @param {string} id - Bare UUID, DIAMOND/{uuid}, or one-safe-decode form.
   * @param {Object} [requestOptions] - Optional fetch options. `signal`
   *                              (AbortSignal) cancels this request only.
   * @returns {Promise<*>} Parsed JSON Compare response.
   */
  function getDiamondCompare(id, requestOptions) {
    requestOptions = requestOptions || {};
    var identity = normalizeDiamondId(id);
    return request(DIAMONDS_PATH + '/compare', {
      method: 'GET',
      params: { id: identity.bareUuid },
      signal: requestOptions.signal,
    });
  }

  /**
   * Sends a Drop a Hint request via POST /drop-a-hint.
   * Body is JSON. Callers must pass only the approved payload fields.
   * Structured backend errors stay on IvanovAPIError.data (e.g. data.error.code).
   *
   * @param {Object} payload - { diamondId, recipientEmail, senderName, senderEmail, message, sendCopy }
   * @param {Object} [requestOptions] - Optional fetch options. `signal` cancels this request only.
   * @returns {Promise<*>} Parsed JSON response.
   */
  function sendDropAHint(payload, requestOptions) {
    requestOptions = requestOptions || {};
    return request(DROP_A_HINT_PATH, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: payload == null ? {} : payload,
      signal: requestOptions.signal,
    });
  }

  /**
   * Looks up many saved diamonds via POST /diamonds/batch.
   * Returns the parsed envelope as-is. Does not normalize, drop, or
   * rewrite ids — per-row invalid/not_found/error handling belongs to
   * the backend and to the caller.
   *
   * @param {Array} ids - Saved diamond ids, already in the caller's form.
   * @param {Object} [requestOptions] - Optional fetch options. `signal`
   *                              (AbortSignal) cancels this request only.
   * @returns {Promise<*>} Parsed JSON batch envelope.
   */
  function getDiamondsBatch(ids, requestOptions) {
    requestOptions = requestOptions || {};
    return request(DIAMONDS_PATH + '/batch', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: { ids: ids },
      signal: requestOptions.signal,
    });
  }

  // ------------------------------------------------------------------
  // Public API surface. Keep this intentionally minimal.
  // ------------------------------------------------------------------
  window.IvanovAPI = {
    searchDiamonds: searchDiamonds,
    normalizeDiamondId: normalizeDiamondId,
    buildDiamondDetailUrl: buildDiamondDetailUrl,
    getDiamond: getDiamond,
    getDiamondCompare: getDiamondCompare,
    getDiamondsBatch: getDiamondsBatch,
    sendDropAHint: sendDropAHint,
  };
})(window);
